import * as React from 'react';
import ModalDialog, {
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import SectionMessage from '@atlaskit/section-message';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import Spinner from '@atlaskit/spinner';
import Button from '@atlaskit/button';
import { css } from '@emotion/core';

import meta from '@/meta';
import { useFetchErrorController, useModalController, useSuccessController } from '@/controllers';
import { apis, apiResolve, apiFetch } from '@/services';
import { querySpec } from '@/services/api-service/api-queries';
import { spaceTopCozy } from '@/styles';
import { SloIncidents } from '@/ui';
import ExternalLinkAction from '@/components/primitive/external-link';
import { DAC_DOCS_BASE_URL } from '@/constants';

import Incidents from '../incidents';

const IncidentsTableContainerStyles = css`
  & > div {
    margin-bottom: 0;
  }
`;

const DeleteModal: React.FC = () => {
  const setSuccessMessage = useSuccessController();
  const { publicId, type, isOpen, onClose, entityName, onDeleteSuccess } = useModalController();
  const ENTITY_TYPE = meta[type].copy.default;

  const queryClient = useQueryClient();
  const { onError: setGlobalError } = useFetchErrorController();

  const [badDeleteMessage, setBadDeleteMessage] = React.useState('');

  const mutation = useMutation((deleteFn: <T>() => Promise<T>) => apiResolve(deleteFn()), {
    onError: setGlobalError,
    onSuccess: (_data, _variables, _context) => {
      queryClient.invalidateQueries({ queryKey: [type] });
      // Also invalidate queries to reflect slo changes in search results
      queryClient.invalidateQueries({ queryKey: ['capability'] });
      queryClient.invalidateQueries({ queryKey: ['search'] });

      onDeleteSuccess();
    },
  });

  const handleClose = React.useCallback(() => {
    setBadDeleteMessage('');
    mutation.reset();
    onClose();
  }, [onClose, mutation]);

  async function handleDelete() {
    let deleteFn;
    const params = { publicId };
    switch (type) {
      case 'team':
        deleteFn = apis.team.deleteTeam.bind(apis.team, params);
        break;
      case 'product':
        deleteFn = apis.product.deleteProduct.bind(apis.product, params);
        break;
      case 'capability':
        deleteFn = apis.capability.deleteCapability.bind(apis.capability, params);
        break;
      case 'slo':
        deleteFn = apis.slo.deleteSLO.bind(apis.slo, params);
        break;
    }
    mutation.mutate(deleteFn);
  }

  React.useEffect(() => {
    if (mutation.isSuccess) {
      setSuccessMessage({
        title: `${ENTITY_TYPE} deleted`,
        message: `And it's gone. We've successfully deleted that ${type}. Gone. No more.`,
      });
      handleClose();
    } else if (mutation.error) {
      setBadDeleteMessage(mutation.error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.isSuccess, mutation.error, ENTITY_TYPE, type]);

  // Fetch SLO level incidents if it is attempting to delete an SLO
  const { queryKey: sloIncidentsKey, path: sloIncidentsPath } = querySpec('hot.slo.{id}', {
    id: publicId,
  });
  const { isInitialLoading: isSloIncidentsLoading, data: sloIncidents = [] } = useQuery(
    sloIncidentsKey,
    () => apiFetch(sloIncidentsPath),
    {
      enabled: isOpen && type === 'slo',
      onError: setGlobalError,
    }
  );

  // Fetch Capability level incidents if it is attempting to delete a capability
  const { queryKey: incidentsKey, path: incidentsPath } = querySpec('hot.search.{id}', {
    id: publicId,
  });
  const {
    isInitialLoading: isCapabilityIncidentsLoading,
    data: capabilityIncidentsData = {},
  } = useQuery(incidentsKey, () => apiFetch(incidentsPath), {
    enabled: isOpen && type === 'capability',
    onError: setGlobalError,
  });
  const capabilityIncidents: SloIncidents = capabilityIncidentsData?.content || [];
  const hasIncidents: boolean = sloIncidents.length || capabilityIncidents.length;
  const canDelete: boolean =
    !isSloIncidentsLoading && !isCapabilityIncidentsLoading && !hasIncidents;

  return isOpen ? (
    <ModalDialog onClose={handleClose}>
      <ModalHeader>
        <ModalTitle appearance="danger">Confirm Delete</ModalTitle>
      </ModalHeader>
      <ModalBody>
        {badDeleteMessage && (
          <SectionMessage appearance="warning">{badDeleteMessage}</SectionMessage>
        )}
        <p data-testid="delete-modal-dialog-text">
          Deleting{' '}
          {entityName && (
            <>
              your {meta[type].copy.default}, &quot;<strong>{entityName}</strong>&quot;
            </>
          )}
          . {canDelete ? `Once you delete, it's gone for good.` : ''}
        </p>
        {!canDelete && (
          <div css={spaceTopCozy}>
            {!isSloIncidentsLoading && !isCapabilityIncidentsLoading && (
              <SectionMessage
                appearance="warning"
                title="Deleting is restricted"
                actions={[
                  <ExternalLinkAction
                    key="a-unassociate-hot"
                    text="See how to unassociate incidents"
                    href={`${DAC_DOCS_BASE_URL}hot-association/#how-to-unassociate-an-incident-from-an-slo`}
                  />,
                ]}
              >
                <p>
                  You can&apos;t delete this {ENTITY_TYPE} because it still has associated
                  incidents.
                </p>
              </SectionMessage>
            )}
            <div css={[spaceTopCozy, IncidentsTableContainerStyles]}>
              {type === 'slo' && (
                <Incidents data={sloIncidents} isLoading={isSloIncidentsLoading} size="compact" />
              )}
              {type === 'capability' && (
                <Incidents
                  data={capabilityIncidents}
                  isLoading={isCapabilityIncidentsLoading}
                  size="compact"
                />
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button appearance="subtle" onClick={handleClose} isDisabled={mutation.isLoading}>
          Cancel
        </Button>
        <Button
          appearance="warning"
          onClick={handleDelete}
          overlay={mutation.isLoading && <Spinner />}
          autoFocus
          isDisabled={!canDelete}
        >
          Delete
        </Button>
      </ModalFooter>
    </ModalDialog>
  ) : null;
};

export default DeleteModal;
